import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatINR, imgUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Heart, BedDouble, Bath, Car, Maximize, MapPin, Sofa, Phone, User, ArrowLeft, CheckCircle2, Loader2, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, favorites, toggleFavorite, login } = useAuth();
  const [property, setProperty] = useState(null);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => {
      setProperty(r.data);
      setForm((f) => ({ ...f, message: `Hi, I'm interested in "${r.data.title}". Please share more details.` }));
    }).catch(() => navigate("/properties"));
  }, [id, navigate]);

  if (!property) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;
  }

  const isFav = favorites.includes(property.id);

  const handleFav = async () => {
    if (!user) { toast.error("Please sign in to save"); login(); return; }
    const f = await toggleFavorite(property.id);
    toast.success(f ? "Added to shortlist" : "Removed from shortlist");
  };

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { property_id: property.id, ...form });
      toast.success("Enquiry sent! The owner will reach out soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy mb-4">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Gallery */}
      <div className="grid grid-cols-4 gap-3 h-[300px] sm:h-[440px]">
        <div className="col-span-4 sm:col-span-3 rounded-2xl overflow-hidden">
          <img src={imgUrl(property.images?.[active])} alt={property.title} className="w-full h-full object-cover" data-testid="gallery-main" />
        </div>
        <div className="hidden sm:flex col-span-1 flex-col gap-3">
          {property.images?.slice(0, 3).map((img, i) => (
            <button
              key={i}
              data-testid={`gallery-thumb-${i}`}
              onClick={() => setActive(i)}
              className={`flex-1 rounded-xl overflow-hidden border-2 transition-all ${active === i ? "border-navy" : "border-transparent"}`}
            >
              <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        {/* Main info */}
        <div className="lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Badge className="bg-navy hover:bg-navy">{property.listing_type === "rent" ? "For Rent" : "For Sale"}</Badge>
                <Badge variant="outline" className="border-slate-300 text-slate-600">{property.property_type}</Badge>
              </div>
              <h1 className="mt-3 font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{property.title}</h1>
              <p className="mt-2 flex items-center text-slate-500"><MapPin className="h-4 w-4 mr-1" /> {property.locality}, {property.city}{property.state ? `, ${property.state}` : ""}</p>
            </div>
            <button data-testid="detail-fav-btn" onClick={handleFav} className="h-11 w-11 rounded-full border border-slate-200 flex items-center justify-center hover:border-rose-300 shrink-0">
              <Heart className={`h-5 w-5 ${isFav ? "fill-gold text-gold-dark" : "text-slate-500"}`} />
            </button>
          </div>

          <p className="mt-5 font-display text-4xl font-bold text-navy">
            {formatINR(property.price)}
            {property.listing_type === "rent" && <span className="text-lg font-medium text-slate-500">/month</span>}
          </p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-5 gap-4">
            {[
              { icon: BedDouble, label: "Bedrooms", value: `${property.bhk} BHK` },
              { icon: Bath, label: "Bathrooms", value: property.bathrooms },
              { icon: Car, label: "Car Spaces", value: property.car_spaces ?? 1 },
              { icon: Maximize, label: "Area", value: `${property.area_sqft} sqft` },
              { icon: Sofa, label: "Furnishing", value: property.furnishing },
            ].map((s) => (
              <div key={s.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <s.icon className="h-5 w-5 text-navy" />
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">{s.label}</p>
                <p className="font-display font-semibold text-slate-900">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-slate-900">About this property</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">{property.description}</p>
          </div>

          {property.amenities?.length > 0 && (
            <div className="mt-8">
              <h2 className="font-display text-xl font-semibold text-slate-900">Amenities</h2>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {property.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-slate-700">
                    <CheckCircle2 className="h-4 w-4 text-navy" /> <span className="text-sm">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark">Contact</p>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-navy flex items-center justify-center text-white">
                <User className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display font-semibold text-slate-900 flex items-center gap-1.5">
                  {property.contact_name}
                  {property.owner_verified && <BadgeCheck className="h-4 w-4 text-navy" title="Verified" />}
                </p>
                <p className="text-sm text-slate-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {property.contact_phone}</p>
              </div>
            </div>

            <form onSubmit={submitEnquiry} className="mt-6 space-y-3">
              <div>
                <Label className="text-xs">Your Name</Label>
                <Input data-testid="enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input data-testid="enquiry-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input data-testid="enquiry-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 …" />
              </div>
              <div>
                <Label className="text-xs">Message</Label>
                <Textarea data-testid="enquiry-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
              </div>
              <Button data-testid="enquiry-submit" type="submit" disabled={sending} className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Enquiry"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
